import { normalizeTarget, type Party } from '../../common';
import { getObjectId, type Id } from '../../validation';
import type { Subscription } from './subscription.model';
import {
  DEFAULT_SUBSCRIPTION_MODEL,
  type SubscriptionGrant,
  type SubscriptionModel,
} from './subscription.usage.model';

/**
 * The handle callers pass when they want to act on a subscription they
 * have not loaded yet — subscribe, pay, check entitlement, spend a
 * credit.
 *
 * **Money is keyed to the `User`, never to the `Driver`.** A person can
 * hold a Driver row per employer and gain or lose them over time, so
 * filing the money against one of those rows means a freelancer who
 * later joins a company is a different customer to the one who bought
 * the credits. The login is the person, and the person is who paid.
 * `Driver` stays the key only where the field is genuinely about
 * driving — `Vehicle.driver`, `Order.driver`, the name on the
 * government waybill. Both ids travel together regardless:
 * `Party.ids[]` carries `user_id` and `driver_id`, so a driver-shaped
 * question can still be answered off a money-shaped party.
 *
 * `target` tells the resolver which collection to read, and it defaults
 * to `'org'` at every boundary that accepts one so that the company
 * path keeps working untouched. Reads by id do not need it at all:
 * ObjectIds are unique across collections, so `owner._id` alone finds
 * the document.
 */
export type SubscriptionOwnerRef = {
  _id: Id;
  /** Which collection holds the owner. Absent means an Org. */
  target?: 'org' | 'user';
};

/**
 * Which model this subscription is on, with the back-compat default
 * applied. Read this instead of `sub.model` so the "absent means
 * period" rule lives in one place.
 */
export const getSubscriptionModel = (sub: Pick<Subscription, 'model'>): SubscriptionModel =>
  sub.model ?? DEFAULT_SUBSCRIPTION_MODEL;

/** Prepaid credit bucket — no interval, no renewal, no dunning. */
export const isUsageSubscription = (sub: Pick<Subscription, 'model'>): boolean =>
  getSubscriptionModel(sub) === 'usage';

/** Renews on an interval — the original model. */
export const isPeriodSubscription = (sub: Pick<Subscription, 'model'>): boolean =>
  getSubscriptionModel(sub) === 'period';

/**
 * Which price list this owner buys from — `'freelancer'` for an
 * owner-operator, `'org'` for a company.
 *
 * Returns the string rather than `SubscriptionAudience` from `@hero/core`
 * because `@hero/schema` sits below `@hero/core` in the dependency
 * order; the two are kept in step by the union being one shared pair of
 * literals. Callers in `@hero/core` and above can assign the result
 * straight to `SubscriptionAudience`.
 *
 * Derived from the owner Party rather than stored, so it cannot drift
 * from who actually holds the grant. Anything that is not a `driver`
 * reads as a company: a `client` or `contact` owner would be a bug, and
 * quoting them company prices is the conservative failure.
 */
export const getSubscriptionAudience = (sub: Pick<Subscription, 'owner'>): 'org' | 'freelancer' =>
  isFreelancerOwnerParty(sub.owner) ? 'freelancer' : 'org';

/**
 * The owner of a loaded subscription, as the ref other services take.
 *
 * Collapses the Party's full `Target` union down to the two values a ref
 * can hold, rather than making each caller narrow it: everything that is
 * not an org is a person, and a person is named by their login.
 */
export const getSubscriptionOwnerRef = (sub: Pick<Subscription, 'owner'>): SubscriptionOwnerRef =>
  subscriptionOwnerRefOf(sub.owner) ?? { _id: sub.owner._id, target: 'org' };

/**
 * The same answer from a bare {@link SubscriptionOwnerRef}, for the
 * paths that are quoting a price *before* any subscription exists.
 */
export const getAudienceForOwnerRef = (ref: SubscriptionOwnerRef): 'org' | 'freelancer' =>
  ref.target === 'org' || ref.target === undefined ? 'org' : 'freelancer';

/**
 * Whether a Party is a freelancer owner-operator — a row in the
 * `Driver` collection standing on its own rather than a company.
 *
 * **Driver-shaped question.** Ask it of an order's shipper when you need
 * the person who hauls: the audit anchor on a government waybill, the
 * driver named on a trip. For "who pays for this" ask
 * {@link isFreelancerOwnerParty} instead — the two used to be the same
 * predicate, and that is exactly why the money ended up filed against a
 * Driver row.
 */
export const isFreelancerParty = (party: Pick<Party, 'target'>): boolean =>
  normalizeTarget(party.target) === 'driver';

/**
 * Whether this party buys from the freelancer price list.
 *
 * A `user` who drives for themselves and a legacy `driver`-keyed owner
 * are the same customer, so both read as a freelancer. A `user` with no
 * driver role is an individual customer, who buys nothing today and
 * gets the company answer — the conservative failure, same as before.
 */
export const isFreelancerOwnerParty = (party: Pick<Party, 'target' | 'roles'>): boolean => {
  const target = normalizeTarget(party.target);
  if (target === 'driver') return true;
  return target === 'user' && (party.roles ?? []).includes('driver');
};

/**
 * The money-side handle for any party, or `undefined` when there is
 * nobody to bill.
 *
 * The one place a Party becomes a {@link SubscriptionOwnerRef}, so that
 * "who pays" cannot be answered two ways. A `driver` party resolves to
 * the login behind it, read off `ids[]` — every Driver party we
 * materialize carries `user_id`. One that does not is an app-less
 * driver a fleet added: no login, nothing to charge, and `undefined`
 * says so rather than filing money against a row that cannot pay.
 *
 * A `client`, `contact` or `profile` party is CRM-only — someone named
 * on a load who never signed up — and is likewise nobody to bill.
 */
export const subscriptionOwnerRefOf = (
  party: Pick<Party, '_id' | 'target' | 'ids'>,
): SubscriptionOwnerRef | undefined => {
  const target = normalizeTarget(party.target);
  if (target === 'org') return { _id: getObjectId(party._id), target: 'org' };
  if (target === 'user') return { _id: getObjectId(party._id), target: 'user' };
  if (target !== 'driver') return undefined;

  const userId = party.ids?.find(entry => entry.type === 'user_id')?.id;
  return userId ? { _id: getObjectId(userId), target: 'user' } : undefined;
};

/** Credits left to spend. `0` for anything that is not a usage sub. */
export const getSubscriptionCreditsRemaining = (
  sub: Pick<Subscription, 'model' | 'balance'>,
): number => (isUsageSubscription(sub) ? sub.balance?.remaining ?? 0 : 0);

/**
 * Whether a grant is still live — not revoked, not expired, and either
 * open-ended or with an `expiresAt` still in the future.
 */
export const isGrantActive = (grant: SubscriptionGrant, now: Date = new Date()): boolean =>
  !grant.revokedAt &&
  !grant.expiredAt &&
  (!grant.expiresAt || new Date(grant.expiresAt).getTime() > now.getTime());

/** Active grants of one mode, or all modes when `mode` is omitted. */
export const getActiveGrants = (
  sub: Pick<Subscription, 'balance'>,
  opts: { mode?: SubscriptionGrant['mode']; now?: Date } = {},
): SubscriptionGrant[] => {
  const now = opts.now ?? new Date();
  return (sub.balance?.grants ?? []).filter(
    grant => isGrantActive(grant, now) && (!opts.mode || grant.mode === opts.mode),
  );
};

/** The live free-period grant, if any — started and not yet over, or not started yet. */
export const getActiveWindowGrant = (
  sub: Pick<Subscription, 'balance'>,
  now: Date = new Date(),
): SubscriptionGrant | undefined => getActiveGrants(sub, { now }).find(grant => grant.window);

/** When a free-period grant ends — its `expiresAt`, or `days` from `now` when its clock has not started. */
export const windowGrantEndsAt = (grant: SubscriptionGrant, now: Date = new Date()): Date | undefined => {
  if (!grant.window) return undefined;
  if (grant.expiresAt) return new Date(grant.expiresAt);
  return new Date(now.getTime() + grant.window.days * 24 * 60 * 60 * 1000);
};

/** Sum of the `qty` on active `repayable` grants — how far the overdraft may run. */
export const getActiveRepayableAllowance = (
  sub: Pick<Subscription, 'balance'>,
  now: Date = new Date(),
): number =>
  getActiveGrants(sub, { mode: 'repayable', now }).reduce((total, grant) => total + grant.qty, 0);

/**
 * The lowest `balance.remaining` a debit may leave behind.
 *
 * Derived from the active `repayable` grants when the doc has any
 * grants at all; otherwise falls back to `-fallbackQty` (the
 * PostHog/config default) so legacy free buckets written before grants
 * existed keep their old overdraft behaviour.
 */
export const getRepayableFloor = (
  sub: Pick<Subscription, 'balance'>,
  fallbackQty: number,
  now: Date = new Date(),
): number => {
  const grants = sub.balance?.grants ?? [];
  if (grants.length === 0) return -Math.max(0, fallbackQty);
  return -getActiveRepayableAllowance(sub, now);
};

/** Unspent gifted (`free`) credits currently counted into `remaining`. */
export const getFreeGranted = (sub: Pick<Subscription, 'balance'>): number =>
  sub.balance?.granted ?? 0;

/**
 * The period window, for the paths that cannot run without one.
 *
 * `interval` and the period dates are optional on the document because
 * a usage subscription genuinely has none, which leaves every period
 * path — renewal, proration, metering — holding `Date | undefined` for
 * something it is about to do date arithmetic with. This narrows once,
 * loudly, at the top of those paths.
 *
 * Throwing is the right failure here rather than defaulting: a period
 * subscription with no window is corrupt data, and inventing `now` for
 * it would silently bill a wrong window. A *usage* subscription
 * reaching a period path is a routing bug in the caller, and the error
 * should point at the caller rather than at whatever the missing date
 * broke three frames later.
 *
 * A bare `Error` rather than one of the sales error ids because both
 * causes are invariant violations with no user-facing remedy — nothing
 * the caller could have typed differently produces them, so there is
 * no message worth rendering. Callers that *can* be reached with the
 * wrong model by a user (an API taking a subscription id) should check
 * {@link isUsageSubscription} first and raise `subscription_wrong_model`.
 */
export const requireSubscriptionPeriod = (
  sub: Pick<Subscription, 'model' | 'interval' | 'currentPeriodStart' | 'currentPeriodEnd'>,
): {
  interval: NonNullable<Subscription['interval']>;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
} => {
  const { interval, currentPeriodStart, currentPeriodEnd } = sub;
  if (!interval || !currentPeriodStart || !currentPeriodEnd) {
    throw new Error(
      `subscription has no billing period (model=${getSubscriptionModel(sub)}) — ` +
        'period-only code path reached with a usage subscription, or the document is corrupt',
    );
  }
  return { interval, currentPeriodStart, currentPeriodEnd };
};
